<script setup lang="ts">
/** Add or change photos: a file input plus the rights basis (ReferenceEvidence.images[].rightsBasis). */
import { ref } from 'vue';
import Icon from './Icon.vue';
import AppDialog from './AppDialog.vue';
import { ui } from '@/app/state';
import { addPhotos, RIGHTS_BASES } from '@/app/analysis';

const files = ref<File[]>([]);
const rights = ref<string>(RIGHTS_BASES[1]);
const mode = ref<'replace' | 'add'>('replace');
function onFiles(e: Event) { files.value = [...((e.target as HTMLInputElement).files ?? [])]; }
function close() { ui.pickerOpen = false; files.value = []; }
function use() {
  const f = files.value.slice();
  const r = rights.value;
  const replace = mode.value === 'replace';
  close();
  void addPhotos(f, r, { replace });
}
</script>

<template>
  <AppDialog :open="ui.pickerOpen" labelledby="pickerTitle" @close="close">
    <div class="dlg-head">
      <h2 id="pickerTitle" class="h-display">Choose photos</h2>
      <button type="button" class="icon-btn" data-close aria-label="Close" @click="close"><Icon name="close" /></button>
    </div>
    <p class="dlg-lede">Two or three photos work best: the front, an angle and a close-up. JPEG, PNG or WebP up to 15 MB each.</p>
    <label class="lbl" for="photoFiles"><Icon name="image" /> Photos</label>
    <input id="photoFiles" class="input" type="file" accept="image/jpeg,image/png,image/webp" multiple @change="onFiles">
    <p class="hint">{{ files.length ? `${files.length} selected: ${files.map((f) => f.name).join(', ')}` : 'Nothing selected yet.' }}</p>
    <label class="lbl lbl--gap" for="rightsBasis">Where the photos come from</label>
    <select id="rightsBasis" v-model="rights" class="input">
      <option v-for="r in RIGHTS_BASES" :key="r" :value="r">{{ r }}</option>
    </select>
    <fieldset v-if="ui.ref.images.length" class="radio-row radio-row--gap">
      <legend class="lbl">Existing photos</legend>
      <label><input v-model="mode" type="radio" name="photoMode" value="replace"> Replace them</label>
      <label><input v-model="mode" type="radio" name="photoMode" value="add"> Add to them</label>
    </fieldset>
    <div class="dlg-acts">
      <button type="button" class="btn btn--quiet" data-close @click="close">Cancel</button>
      <button type="button" class="btn btn--ink" data-act="use" :aria-disabled="files.length ? undefined : 'true'" @click="files.length && use()">Use these photos</button>
    </div>
  </AppDialog>
</template>
